/**
 * Leave Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as leaveService from '../api/services/leave.service';
import type {
  LeaveApplyRequest,
  CalculateHoursRequest,
} from '../types';
import { toast } from '../stores/toast.store';

const KEYS = {
  init: ['leave', 'init'] as const,
  balance: ['leave', 'balance'] as const,
  allocations: ['leave', 'allocations'] as const,
  applications: ['leave', 'applications'] as const,
  application: (id: number) => ['leave', 'applications', id] as const,
  substitutes: ['leave', 'substitutes'] as const,
};

/**
 * Hook for fetching leave init data (balance + types + time options)
 */
export function useLeaveInit() {
  return useQuery({
    queryKey: KEYS.init,
    queryFn: async () => {
      const result = await leaveService.getLeaveInit();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch leave data');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching all allocations (including expired)
 */
export function useAllocations() {
  return useQuery({
    queryKey: KEYS.allocations,
    queryFn: async () => {
      const result = await leaveService.getAllocations();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch allocations');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching leave balance
 */
export function useLeaveBalance() {
  return useQuery({
    queryKey: KEYS.balance,
    queryFn: async () => {
      const result = await leaveService.getLeaveBalance();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch balance');
      }
      return result.data!;
    },
  });
}

interface UseLeaveApplicationsParams {
  offset?: number;
  limit?: number;
  state?: string;
}

/**
 * Hook for fetching leave applications list
 */
export function useLeaveApplications(params: UseLeaveApplicationsParams = {}) {
  return useQuery({
    queryKey: [...KEYS.applications, params],
    queryFn: async () => {
      const result = await leaveService.getLeaveApplications(params);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch applications');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching single leave application
 */
export function useLeaveApplication(id: number | undefined) {
  return useQuery({
    queryKey: KEYS.application(id!),
    queryFn: async () => {
      const result = await leaveService.getLeaveApplication(id!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch application');
      }
      return result.data!;
    },
    enabled: !!id,
  });
}

/**
 * Hook for fetching substitutes
 */
export function useSubstitutes() {
  return useQuery({
    queryKey: KEYS.substitutes,
    queryFn: async () => {
      const result = await leaveService.getSubstitutes();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch substitutes');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for calculating leave hours
 */
export function useCalculateHours() {
  return useMutation({
    mutationFn: async (data: CalculateHoursRequest) => {
      const result = await leaveService.calculateHours(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to calculate hours');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for creating leave application
 */
export function useCreateLeave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LeaveApplyRequest) => {
      const result = await leaveService.createLeaveApplication(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create application');
      }
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.applications });
      queryClient.invalidateQueries({ queryKey: KEYS.balance });
      toast.success('請假申請已建立');
    },
    onError: (error: Error) => {
      toast.error('建立失敗', error.message);
    },
  });
}

/**
 * Hook for submitting leave application
 */
export function useSubmitLeave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await leaveService.submitLeaveApplication(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to submit');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.applications });
      queryClient.invalidateQueries({ queryKey: KEYS.application(data.id) });
      toast.success('請假申請已送出');
    },
    onError: (error: Error) => {
      toast.error('送出失敗', error.message);
    },
  });
}

/**
 * Hook for cancelling leave application
 */
export function useCancelLeave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await leaveService.cancelLeaveApplication(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to cancel');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.applications });
      queryClient.invalidateQueries({ queryKey: KEYS.application(data.id) });
      toast.success('請假申請已撤銷');
    },
    onError: (error: Error) => {
      toast.error('撤銷失敗', error.message);
    },
  });
}

/**
 * Hook for deleting leave application
 */
export function useDeleteLeave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await leaveService.deleteLeaveApplication(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to delete');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.applications });
      queryClient.invalidateQueries({ queryKey: KEYS.balance });
      toast.success('請假申請已刪除');
    },
    onError: (error: Error) => {
      toast.error('刪除失敗', error.message);
    },
  });
}
